import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { dataConnectorsApi } from '@/lib/api/data-connectors.api';

import type {
  CreateDataConnectorDto,
  DataConnectorConfig,
  UpdateDataConnectorDto,
} from '@repo/shared-types';

const KEY = ['data-connectors'];
const POLL_MS = 5000;

const anyRunning = (list: Array<{ status: string }> | undefined) =>
  !!list?.some((c) => c.status === 'running');

export function useDataConnectors() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => dataConnectorsApi.list(),
    refetchInterval: (q) => (anyRunning(q.state.data) ? POLL_MS : false),
  });
}

export function useConnectorRuns(id: string | null) {
  return useQuery({
    queryKey: [...KEY, id, 'runs'],
    queryFn: () => dataConnectorsApi.runs(id as string),
    enabled: !!id,
    refetchInterval: (q) => (anyRunning(q.state.data) ? POLL_MS : false),
  });
}

/** Invalidates connectors and the stock listings (a sync or delete changes both). */
const useInvalidate = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: KEY });
    queryClient.invalidateQueries({ queryKey: ['dealer-kit-stock-listings'] });
  };
};

export function useCreateDataConnector() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (data: CreateDataConnectorDto) => dataConnectorsApi.create(data),
    onSuccess: invalidate,
  });
}

export function useUpdateDataConnector() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDataConnectorDto }) =>
      dataConnectorsApi.update(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteDataConnector() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, deleteListings }: { id: string; deleteListings: boolean }) =>
      dataConnectorsApi.remove(id, deleteListings),
    onSuccess: invalidate,
  });
}

export function useTestDataConnector() {
  return useMutation({
    mutationFn: (body: {
      config: DataConnectorConfig;
      secrets?: Record<string, string>;
      connector_id?: string;
    }) => dataConnectorsApi.test(body),
  });
}

export function useRunDataConnector() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => dataConnectorsApi.run(id),
    onSuccess: invalidate,
  });
}

export function useDecideConnectorRun() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, runId, approve }: { id: string; runId: string; approve: boolean }) =>
      dataConnectorsApi.decide(id, runId, approve),
    onSuccess: invalidate,
  });
}

export function useResumeConnectorRun() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, runId }: { id: string; runId: string }) =>
      dataConnectorsApi.resume(id, runId),
    onSuccess: invalidate,
  });
}
